import { FC, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Button, CircularProgress, debounce, TextField } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { CustomSelectFormik, IOption } from 'components/elements';
import { ICityFormValues } from 'types';
import s from './index.module.scss';

interface CityFormProps {
	buttonText: string;
	initialValues: ICityFormValues;
	onSubmit: (values: ICityFormValues) => void;
	regionOptions: IOption[];
	regionOptionsLoading: boolean;
	onRegionSearch: (search: string) => void;
	prefixOptions: IOption[];
	shortPrefixOptions: IOption[];
	initialRegionTitle?: string;
	onDeleteRequest?: () => void;
	canDelete?: boolean;
}

export const CityForm: FC<CityFormProps> = ({
	buttonText,
	initialValues,
	onSubmit,
	regionOptions,
	regionOptionsLoading,
	onRegionSearch,
	prefixOptions,
	shortPrefixOptions,
	initialRegionTitle = '',
	onDeleteRequest,
	canDelete = false,
}) => {
	const [regionInputValue, setRegionInputValue] = useState(initialRegionTitle);

	useEffect(() => {
		setRegionInputValue(initialRegionTitle);
	}, [initialRegionTitle, initialValues.regionId]);

	const debouncedRegionSearch = useMemo(() => debounce((search: string) => onRegionSearch(search), 400), [onRegionSearch]);

	useEffect(() => {
		return () => {
			debouncedRegionSearch.clear();
		};
	}, [debouncedRegionSearch]);

	return (
		<Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
			{({ values, dirty, setFieldValue }) => {
				const isPrefixValid = prefixOptions.some((option) => option.value === values.prefix);
				const isShortPrefixValid = shortPrefixOptions.some((option) => option.value === values.shorPrefix);
				const isSubmitDisabled =
					!values.name.trim() ||
					!isShortPrefixValid ||
					!isPrefixValid ||
					!values.regionId ||
					!dirty;
				const selectedRegionOption =
					regionOptions.find((option) => option.value === values.regionId) ||
					(values.regionId && initialRegionTitle ? { value: values.regionId, title: initialRegionTitle } : null);

				return (
					<Form className={s.form}>
						<Field label="Назва" name="name" variant="outlined" as={TextField} fullWidth />
						<Field label="Префікс" name="prefix" options={prefixOptions} component={CustomSelectFormik} />
						<Field label="Скорочення" name="shorPrefix" options={shortPrefixOptions} component={CustomSelectFormik} />
						<Autocomplete
							options={regionOptions}
							value={selectedRegionOption}
							inputValue={regionInputValue}
							loading={regionOptionsLoading}
							filterOptions={(options) => options}
							noOptionsText={regionInputValue.trim() ? 'Не знайдено' : 'Почніть вводити регіон'}
							onOpen={() => {
								if (!regionOptions.length) {
									void onRegionSearch(regionInputValue);
								}
							}}
							getOptionLabel={(option) => option.title}
							isOptionEqualToValue={(option, value) => option.value === value.value}
							onChange={(_, value) => {
								setFieldValue('regionId', value?.value || '');

								if (!value) {
									setRegionInputValue('');
								}
							}}
							onInputChange={(_, value, reason) => {
								setRegionInputValue(value);

								if (reason === 'input' || reason === 'clear') {
									debouncedRegionSearch(value);
								}

								if (reason === 'clear') {
									setFieldValue('regionId', '');
								}
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Регіон"
									placeholder="Почніть вводити регіон"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{regionOptionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>

						<div className={s.actions}>
							{/* {canDelete && onDeleteRequest && (
								<Button
									className={s.deleteButton}
									type="button"
									variant="outlined"
									color="secondary"
									onClick={onDeleteRequest}
								>
									Видалити
								</Button>
							)} */}
							<Button type="submit" disabled={isSubmitDisabled} variant="rounded">
								{buttonText}
							</Button>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};
